import React, { useState } from "react";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Typography,
} from "@material-ui/core";
import {
  GetApp as GetAppIcon,
  People as PeopleIcon,
} from "@material-ui/icons";

// components
import PageTitle from "../../components/PageTitle";
import ManageUsersDialog from "../../components/ManageUsersDialog/ManageUsersDialog";

// styles
import useStyles from "./styles";

// constants
import { SERVER } from "../../constants";

// context
import {
  useGroupState,
  useGroupDispatch,
  setScoringMode,
} from "../../context/GroupContext";

// localization
import { useTranslation, useLeagueNames } from "../../localization";

// Luxon
const { DateTime } = require("luxon");

// Quote a field if it contains a comma, quote, or newline, doubling any inner
// quotes. Required because `points_breakdown` ("10CR, 1HG, 1AG") contains commas.
function csvEscape(value) {
  var s = value === undefined || value === null ? "" : String(value);
  if (/[",\n]/.test(s)) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}

// A match whose predictions are revealed: in progress or finished. Mirrors the
// same helper on the Calendar page. Everything else is an upcoming/future match.
function isLockedResult(fixture) {
  return fixture.live || fixture.status === "post";
}

export default function Admin() {
  var classes = useStyles();
  var t = useTranslation();
  var leagueOverrides = useLeagueNames();
  var { selectedGroup, leagueNames, scoringMode, scoringModes } =
    useGroupState();
  var dispatch = useGroupDispatch();
  const [isExporting, setIsExporting] = useState(false);
  const [isSavingMode, setIsSavingMode] = useState(false);
  const [toast, setToast] = useState("");
  const [usersOpen, setUsersOpen] = useState(false);
  const [missingOpen, setMissingOpen] = useState(false);
  const [includePast, setIncludePast] = useState(false);
  const [includeFuture, setIncludeFuture] = useState(true);
  const [isExportingMissing, setIsExportingMissing] = useState(false);

  // CSV column headers, in the order requested. "league" is prepended because a
  // group's past matches span multiple leagues that would otherwise be
  // indistinguishable once interleaved by date.
  var COLUMNS = t.admin.csvColumns;

  function leagueLabel(fixture) {
    return (
      leagueOverrides[fixture.league_name] ||
      leagueNames[fixture.league_name] ||
      fixture.official_league_name ||
      fixture.league_name
    );
  }

  // Flatten /allinfo fixtures into one CSV row per (scored fixture, player
  // prediction), sorted by kickoff date then alphabetically by player name.
  function buildRows(allInfo) {
    var fixtures = (allInfo && allInfo.fixtures) || [];
    var rows = [];
    fixtures.forEach(fixture => {
      // Predictions and points only exist once a match is scored (past matches).
      if (!("home_score" in fixture)) {
        return;
      }
      (fixture.predictions || []).forEach(p => {
        rows.push({ fixture, p });
      });
    });
    rows.sort((a, b) => {
      if (a.fixture.date !== b.fixture.date) {
        return a.fixture.date < b.fixture.date ? -1 : 1;
      }
      return a.p.name.localeCompare(b.p.name);
    });
    return rows.map(({ fixture, p }) => [
      leagueLabel(fixture),
      DateTime.fromISO(fixture.date, { zone: "utc" })
        .toLocal()
        .toFormat("yyyy-MM-dd HH:mm"),
      fixture.home_team,
      fixture.away_team,
      fixture.home_score,
      fixture.away_score,
      p.name,
      p.home_score,
      p.away_score,
      p.points !== undefined && p.points !== null ? p.points : "",
      p.points_breakdown || "",
    ]);
  }

  // Build one CSV row per match (in the selected categories) listing the group
  // players with no prediction for it. /allinfo carries per-player predictions
  // only for locked matches, so future matches are resolved against the separate
  // /predictions map (same rule the Calendar page uses to flag missing players).
  function buildMissingRows(allInfo, predictionsMap) {
    var fixtures = (allInfo && allInfo.fixtures) || [];
    var leagues = (allInfo && allInfo.leagues) || [];
    var rows = fixtures.filter(fixture => {
      var locked = isLockedResult(fixture);
      return locked ? includePast : includeFuture;
    });
    rows.sort((a, b) => {
      if (a.date !== b.date) {
        return a.date < b.date ? -1 : 1;
      }
      return (a.home_team || "").localeCompare(b.home_team || "");
    });
    return rows.map(fixture => {
      var league = leagues.find(l => l.name === fixture.league_name);
      var players = (league && league.players) || [];
      var missing;
      if (isLockedResult(fixture)) {
        var predicted = new Set(
          (fixture.predictions || []).map(p => p.username),
        );
        missing = players.filter(p => !predicted.has(p.username));
      } else {
        var leaguePreds = predictionsMap[fixture.league_name] || {};
        missing = players.filter(
          p => !(leaguePreds[p.username] && leaguePreds[p.username][fixture.id]),
        );
      }
      return [
        leagueLabel(fixture),
        DateTime.fromISO(fixture.date, { zone: "utc" })
          .toLocal()
          .toFormat("yyyy-MM-dd HH:mm"),
        fixture.home_team,
        fixture.away_team,
        "home_score" in fixture ? fixture.home_score : "",
        "away_score" in fixture ? fixture.away_score : "",
        missing.map(p => p.name).join("\n"),
      ];
    });
  }

  function downloadCsv(rows, columns, filenamePrefix) {
    var lines = [columns, ...rows].map(cols => cols.map(csvEscape).join(","));
    var csv = lines.join("\n");
    var blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    var url = URL.createObjectURL(blob);
    var a = document.createElement("a");
    a.href = url;
    a.download =
      filenamePrefix +
      "-" +
      selectedGroup +
      "-" +
      DateTime.local().toISODate() +
      ".csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function handleExport() {
    setIsExporting(true);
    var url = new URL(SERVER + "/allinfo");
    url.search = new URLSearchParams({ group: selectedGroup }).toString();
    fetch(url)
      .then(r => r.json())
      .then(
        allInfo => {
          downloadCsv(buildRows(allInfo), COLUMNS, "predictions");
          setIsExporting(false);
        },
        error => {
          console.debug(error);
          alert(t.admin.couldNotExport);
          setIsExporting(false);
        },
      );
  }

  function handleExportMissing() {
    setIsExportingMissing(true);
    var allInfoUrl = new URL(SERVER + "/allinfo");
    allInfoUrl.search = new URLSearchParams({ group: selectedGroup }).toString();
    var predictionsUrl = new URL(SERVER + "/predictions");
    predictionsUrl.search = new URLSearchParams({
      group: selectedGroup,
    }).toString();
    Promise.all([
      fetch(allInfoUrl, { cache: "no-store" }).then(r => r.json()),
      fetch(predictionsUrl, { cache: "no-store" }).then(r => r.json()),
    ]).then(
      ([allInfo, predictionsMap]) => {
        downloadCsv(
          buildMissingRows(allInfo, predictionsMap || {}),
          t.admin.csvMissingColumns,
          "missing-predictions",
        );
        setIsExportingMissing(false);
        setMissingOpen(false);
      },
      error => {
        console.debug(error);
        alert(t.admin.couldNotExportMissing);
        setIsExportingMissing(false);
      },
    );
  }

  function handleScoringModeChange(event) {
    var mode = event.target.value;
    if (mode === scoringMode) {
      return;
    }
    setIsSavingMode(true);
    setScoringMode(dispatch, selectedGroup, mode).then(
      () => {
        setIsSavingMode(false);
        setToast(t.admin.scoringSaved);
      },
      error => {
        console.debug(error);
        setIsSavingMode(false);
        setToast(t.admin.couldNotSaveScoring);
      },
    );
  }

  return (
    <>
      <PageTitle title={t.admin.title} />
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Paper className={classes.card}>
            <Typography variant="h6" gutterBottom>
              {t.admin.scoringTitle}
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              className={classes.description}
            >
              {t.admin.scoringDescription}
            </Typography>
            <FormControl
              variant="outlined"
              fullWidth
              disabled={isSavingMode || scoringModes.length === 0}
            >
              <InputLabel id="scoring-mode-label">
                {t.admin.scoringLabel}
              </InputLabel>
              <Select
                labelId="scoring-mode-label"
                label={t.admin.scoringLabel}
                value={scoringMode || ""}
                onChange={handleScoringModeChange}
              >
                {scoringModes.map(mode => (
                  <MenuItem key={mode.name} value={mode.name}>
                    {mode.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {isSavingMode && (
              <CircularProgress
                size={18}
                className={classes.savingSpinner}
              />
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper className={classes.card}>
            <Typography variant="h6" gutterBottom>
              {t.admin.exportTitle}
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              className={classes.description}
            >
              {t.admin.exportDescription}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={
                isExporting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <GetAppIcon />
                )
              }
              disabled={isExporting}
              onClick={handleExport}
            >
              {isExporting ? t.admin.exporting : t.admin.exportTitle}
            </Button>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper className={classes.card}>
            <Typography variant="h6" gutterBottom>
              {t.admin.exportMissingTitle}
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              className={classes.description}
            >
              {t.admin.exportMissingDescription}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetAppIcon />}
              onClick={() => setMissingOpen(true)}
            >
              {t.admin.exportMissingTitle}
            </Button>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper className={classes.card}>
            <Typography variant="h6" gutterBottom>
              {t.admin.usersTitle}
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              className={classes.description}
            >
              {t.admin.usersDescription}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PeopleIcon />}
              onClick={() => setUsersOpen(true)}
            >
              {t.admin.usersTitle}
            </Button>
          </Paper>
        </Grid>
      </Grid>
      <ManageUsersDialog
        open={usersOpen}
        onClose={() => setUsersOpen(false)}
        group={selectedGroup}
      />
      <Dialog
        open={missingOpen}
        onClose={() => !isExportingMissing && setMissingOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t.admin.exportMissingDialogTitle}</DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            color="textSecondary"
            className={classes.description}
          >
            {t.admin.exportMissingDescription}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeFuture}
                color="primary"
                onChange={e => setIncludeFuture(e.target.checked)}
              />
            }
            label={t.admin.exportMissingFuture}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={includePast}
                color="primary"
                onChange={e => setIncludePast(e.target.checked)}
              />
            }
            label={t.admin.exportMissingPast}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setMissingOpen(false)}
            color="secondary"
            disabled={isExportingMissing}
          >
            {t.common.cancel}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={
              isExportingMissing ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <GetAppIcon />
              )
            }
            disabled={
              isExportingMissing || (!includePast && !includeFuture)
            }
            onClick={handleExportMissing}
          >
            {isExportingMissing ? t.admin.exporting : t.admin.exportMissingTitle}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast("")}
        message={toast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
}
